import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Typography, Alert, Tooltip } from '@mui/material';

import { DataTable } from '../components/DataTable';
import { meshesApi } from '../api/services';
import { timeAgo } from '../utils/timeAgo';
import type { Mesh } from '../api/config';

// Meshes (sc-6059): org-scoped groups of interoperating branch locations that
// share inventory. Read-only since sc-6111: rows mirror CRM districts and the
// nightly sync is the only writer, so this page is a pure table — no create,
// edit, delete, or membership actions. Region/Managers/Synced surface the CRM
// provenance for cross-referencing.
const EMPTY_CELL = (
  <Typography variant="body2" color="text.secondary">
    —
  </Typography>
);

export const MeshesPage: React.FC = () => {
  const { data: meshes = [], isLoading, error } = useQuery({
    queryKey: ['meshes'],
    queryFn: () => meshesApi.getAll(),
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 35 },
    { field: 'name', headerName: 'Name', width: 220 },
    {
      field: 'organization',
      headerName: 'Organization',
      width: 200,
      valueGetter: (params: GridRenderCellParams<Mesh>) => params.row?.organization?.name || '',
    },
    {
      field: 'region_name',
      headerName: 'Region',
      width: 150,
      valueGetter: (params: GridRenderCellParams<Mesh>) => params.row?.region_name || '',
      renderCell: (params: GridRenderCellParams<Mesh>) =>
        params.row?.region_name ? (
          <Typography variant="body2">{params.row.region_name}</Typography>
        ) : (
          EMPTY_CELL
        ),
    },
    {
      field: 'managers',
      headerName: 'Managers',
      width: 320,
      sortable: false,
      filterable: false,
      // String value so CSV export / quick filter see the lines, not a
      // stringified object array. Display comes from renderCell.
      valueGetter: (params: GridRenderCellParams<Mesh>) =>
        (params.row?.managers || []).map((m) => `${m.name} — ${m.contact_type}`).join(', '),
      renderCell: (params: GridRenderCellParams<Mesh>) => {
        const managers = params.row?.managers || [];
        if (managers.length === 0) return EMPTY_CELL;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, minWidth: 0, width: '100%' }}>
            {managers.map((m) => (
              // Tooltip carries the full line — contact_type suffixes (Ops /
              // Sales / Parts) are the distinguishing part and ellipsize first.
              <Tooltip key={m.contact_id} title={`${m.name} — ${m.contact_type}`} enterDelay={700}>
                <Typography variant="body2" noWrap>
                  {m.name} — {m.contact_type}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'locations',
      headerName: 'Locations',
      // Fixed width (no flex) so the columns pack left. Names stay on one
      // line; an over-long name ellipsizes, full name on hover.
      width: 420,
      sortable: false,
      filterable: false,
      // String value so the toolbar's CSV export / quick filter see names,
      // not a stringified object array. Display comes from renderCell.
      valueGetter: (params: GridRenderCellParams<Mesh>) =>
        (params.row?.locations || []).map((l) => l.name).join(', '),
      renderCell: (params: GridRenderCellParams<Mesh>) => {
        const locations = params.row?.locations || [];
        if (locations.length === 0) {
          return <Typography variant="body2" color="text.secondary">No locations</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5, minWidth: 0, width: '100%' }}>
            {locations.map((l) => (
              <Tooltip key={l.id} title={l.name} enterDelay={700}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={l.active ? {} : { color: 'text.disabled' }}
                >
                  {l.name}{l.active ? '' : ' (inactive)'}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'synced_at',
      headerName: 'Synced',
      width: 130,
      // Raw ISO value keeps sorting chronological; display is the coarse age.
      valueGetter: (params: GridRenderCellParams<Mesh>) => params.row?.synced_at || '',
      renderCell: (params: GridRenderCellParams<Mesh>) => (
        <Tooltip title={params.row?.synced_at ? new Date(params.row.synced_at).toLocaleString() : ''}>
          <Typography variant="body2" color="text.secondary">
            {timeAgo(params.row?.synced_at ?? null)}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  if (error) {
    return <Alert severity="error">Error loading meshes</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Meshes
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Groups of interoperating branches that share inventory. Districts are synced
        nightly from the CRM — names, regions, managers, and membership are managed
        there, not here.
      </Typography>

      <DataTable
        rows={meshes}
        columns={columns}
        loading={isLoading}
        getRowId={(row: Mesh) => row.id.toString()}
        getRowHeight={() => 'auto'}
        dataGridSx={{ '& .MuiDataGrid-row': { minHeight: '52px !important' } }}
        fullHeight
      />
    </Box>
  );
};
